/**
 * Co-Op Battle Page
 *
 * Orchestrates the three battle phases: deployment, combat, and results.
 * Connects to the adventure socket for deployment coordination and battle events.
 *
 * Deployment: Auto-places alive creatures in the player deploy zone and
 * sends placement events to the server. Shows creature list with HP bars.
 *
 * Combat: Renders the battle using CoOpBattleView (PixiJS combat renderer).
 *
 * Results: Shows victory/defeat overlay with creature HP changes.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdventureStore } from '../stores/useAdventureStore';
import { adventureSocket, type AdventureBattleResultPayload } from '../services/adventure-socket';
import { useAuth } from '../contexts/AuthContext';
import { CoOpVictoryOverlay } from '../components/CoOp/CoOpVictoryOverlay';
import { CoOpBattleView } from '../components/CoOp/CoOpBattleView';
import { RendererErrorBoundary } from '../components/ErrorBoundary';
import type { AxialCoordinate } from '@drawn-of-war/shared';
import './CoOpBattlePage.css';

/** Data received from adventure:deploy-combat-starting */
interface CombatStartingData {
  matchId: string;
  battlefieldConfig: {
    width: number;
    height: number;
    orientation?: 'left_vs_right' | 'top_vs_bottom';
    obstacles?: AxialCoordinate[];
    playerDeployCols: number;
    enemyDeployCols: number;
    playerDeployRows?: number;
    enemyDeployRows?: number;
  };
  playerCreatures: Array<{
    instanceId: string;
    creatureId: string;
    name: string;
    position: AxialCoordinate;
    teamId: string;
    ownerId: string;
    health: number;
    maxHealth: number;
  }>;
  enemyCreatures: Array<{
    instanceId: string;
    creatureId: string;
    name: string;
    position: AxialCoordinate;
    teamId: string;
    ownerId: string;
    health: number;
    maxHealth: number;
  }>;
}

export const CoOpBattlePage: React.FC = () => {
  const { adventureId, encounterId } = useParams<{ adventureId: string; encounterId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    adventure,
    world,
    setBattlePhase,
    updateCreatureStates,
    recordEncounterResult,
  } = useAdventureStore();

  const [phase, setPhase] = useState<'deployment' | 'combat' | 'results'>('deployment');
  const [readyState, setReadyState] = useState({ readyCount: 0, neededCount: 2 });
  const [isReady, setIsReady] = useState(false);
  const [battleResult, setBattleResult] = useState<{ victory: boolean; stars: 0 | 1 | 2 | 3 } | null>(null);
  const [combatData, setCombatData] = useState<CombatStartingData | null>(null);
  const [resultPayload, setResultPayload] = useState<AdventureBattleResultPayload | null>(null);
  const autoDeployedRef = useRef(false);

  // Get encounter info from world data
  const encounterInfo = world && encounterId ? world.encounters[encounterId] : null;

  // Get alive creatures for deployment
  const aliveCreatures = adventure
    ? Object.entries(adventure.creatureStates)
        .filter(([, c]) => c.status !== 'dead')
        .map(([id, c]) => ({ id, ...c }))
    : [];

  const myCreatures = aliveCreatures.filter(c => c.ownerId === user?.uid);

  // Auto-deploy creatures on mount
  useEffect(() => {
    if (autoDeployedRef.current || !encounterId || !encounterInfo || myCreatures.length === 0) return;
    autoDeployedRef.current = true;

    // Compute player deploy zone based on battlefield orientation
    const bf = encounterInfo.battlefieldConfig;
    const deployPositions: AxialCoordinate[] = [];
    const obstacleSet = new Set(
      (bf.obstacles || []).map((o: AxialCoordinate) => `${o.q},${o.r}`)
    );

    if (bf.orientation === 'top_vs_bottom') {
      // Bottom rows for player deployment
      const deployRows = bf.playerDeployRows ?? 2;
      const startRow = bf.height - deployRows;
      for (let q = 0; q < bf.width; q++) {
        for (let r = startRow; r < bf.height; r++) {
          if (!obstacleSet.has(`${q},${r}`)) {
            deployPositions.push({ q, r });
          }
        }
      }
    } else {
      // Left columns for player deployment (default)
      const deployCols = bf.playerDeployCols;
      for (let q = 0; q < deployCols; q++) {
        for (let r = 0; r < bf.height; r++) {
          if (!obstacleSet.has(`${q},${r}`)) {
            deployPositions.push({ q, r });
          }
        }
      }
    }

    // Place each alive creature in the deploy zone
    myCreatures.forEach((creature, idx) => {
      if (idx < deployPositions.length) {
        adventureSocket.deployPlace(encounterId, creature.id, deployPositions[idx]);
      }
    });

    console.log(
      `[CoOpBattle] Auto-deployed ${Math.min(myCreatures.length, deployPositions.length)} creatures`
    );
  }, [encounterId, encounterInfo, myCreatures]);

  // Register socket listeners for battle events
  useEffect(() => {
    if (!encounterId) return;

    adventureSocket.onDeployReadyState((data) => {
      setReadyState(data);
    });

    adventureSocket.onDeployCombatStarting((data) => {
      setCombatData(data as unknown as CombatStartingData);
      setPhase('combat');
      setBattlePhase('combat', encounterId, data.matchId);
    });

    adventureSocket.onBattleResult((data) => {
      setResultPayload(data);
      setBattleResult({
        victory: data.victory,
        stars: data.stars,
      });
      setPhase('results');
      setBattlePhase('results', data.encounterId);

      // Update store with results
      recordEncounterResult(data.encounterId, {
        stars: data.stars,
        completedAt: new Date().toISOString(),
        attempts: data.encounterProgress.attempts,
      });

      if (data.updatedCreatureStates) {
        updateCreatureStates(
          data.updatedCreatureStates as Record<string, { currentHp: number; maxHp: number; status: string; ownerId: string }>
        );
      }
    });

    adventureSocket.onBattleCancelled(() => {
      // Battle was cancelled, return to map
      setBattlePhase('none');
      navigate(`/adventure/map/${adventureId}`);
    });

    adventureSocket.onError((data) => {
      console.error('[CoOpBattle] Error:', data.message);
    });

    return () => {
      adventureSocket.clearAdventureListeners();
    };
  }, [
    encounterId,
    adventureId,
    navigate,
    setBattlePhase,
    recordEncounterResult,
    updateCreatureStates,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't disconnect - socket is shared with map page
      adventureSocket.clearAdventureListeners();
    };
  }, []);

  const handleReady = useCallback(() => {
    if (!encounterId) return;
    if (isReady) {
      adventureSocket.deployUnready(encounterId);
      setIsReady(false);
    } else {
      adventureSocket.deployReady(encounterId);
      setIsReady(true);
    }
  }, [encounterId, isReady]);

  const handleReturnToMap = useCallback(() => {
    setBattlePhase('none');
    navigate(`/adventure/map/${adventureId}`);
  }, [adventureId, navigate, setBattlePhase]);

  // Build creature changes for the victory overlay
  const creatureChanges = resultPayload
    ? Object.entries(resultPayload.updatedCreatureStates || {}).map(([id, raw]) => {
        const state = raw as { currentHp: number; maxHp: number; status: string; ownerId: string };
        const prevState = adventure?.creatureStates[id];
        return {
          creatureId: id,
          previousHp: prevState?.currentHp ?? state.currentHp,
          newHp: state.currentHp,
          maxHp: state.maxHp,
          revived: false,
          ownerId: state.ownerId,
        };
      })
    : [];

  // Build all creatures for CoOpBattleView
  const allCombatCreatures = combatData
    ? [...combatData.playerCreatures, ...combatData.enemyCreatures].map(c => ({
        instanceId: c.instanceId,
        name: c.name,
        position: c.position,
        teamId: c.teamId,
        ownerId: c.ownerId,
        health: c.health,
        maxHealth: c.maxHealth,
      }))
    : [];

  return (
    <div className="coop-battle">
      {/* Deployment Phase */}
      {phase === 'deployment' && (
        <div className="deployment-phase">
          <div className="deployment-header">
            <h1>Prepare for Battle</h1>
            {encounterInfo && (
              <p className="encounter-name">{encounterInfo.name}</p>
            )}
          </div>

          <div className="deployment-content">
            <div className="creature-list">
              <h3>Your Creatures ({myCreatures.length} deployed)</h3>
              {myCreatures.length === 0 ? (
                <p className="no-creatures">No alive creatures available</p>
              ) : (
                myCreatures.map(creature => {
                  const hpPercent = creature.maxHp > 0
                    ? (creature.currentHp / creature.maxHp) * 100
                    : 0;

                  return (
                    <div key={creature.id} className="creature-item">
                      <div className="creature-item-info">
                        <span className="creature-item-name">{creature.id.slice(0, 15)}</span>
                        <div className="creature-hp-bar-container">
                          <div
                            className="creature-hp-bar-fill"
                            style={{
                              width: `${hpPercent}%`,
                              background: hpPercent > 50 ? '#4ade80' : hpPercent > 25 ? '#fbbf24' : '#ef4444',
                            }}
                          />
                        </div>
                        <span className="creature-hp-text">
                          {creature.currentHp}/{creature.maxHp} HP
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="ready-section">
              <div className="ready-count">
                {readyState.readyCount} / {readyState.neededCount} players ready
              </div>
              <button
                className={`ready-button ${isReady ? 'ready-button-active' : ''}`}
                onClick={handleReady}
              >
                {isReady ? 'Cancel Ready' : 'Ready!'}
              </button>
              {isReady && readyState.readyCount < readyState.neededCount && (
                <p className="waiting-text">Waiting for partner...</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Combat Phase */}
      {phase === 'combat' && combatData && (
        <div className="combat-phase">
          <div className="combat-header">
            <h1>Battle in Progress</h1>
            {encounterInfo && (
              <p className="encounter-name">{encounterInfo.name}</p>
            )}
          </div>
          <div className="combat-renderer-container">
            <RendererErrorBoundary label="Co-Op Combat Renderer">
              <CoOpBattleView
                matchId={combatData.matchId}
                currentUserId={user?.uid || ''}
                battlefieldConfig={{
                  width: combatData.battlefieldConfig.width,
                  height: combatData.battlefieldConfig.height,
                  obstacles: combatData.battlefieldConfig.obstacles,
                }}
                creatures={allCombatCreatures}
              />
            </RendererErrorBoundary>
          </div>
        </div>
      )}

      {/* Combat Phase - no data yet */}
      {phase === 'combat' && !combatData && (
        <div className="combat-phase">
          <div className="combat-placeholder">
            <div className="combat-spinner" />
            <p>Initializing combat...</p>
          </div>
        </div>
      )}

      {/* Results Phase */}
      {phase === 'results' && battleResult && (
        <div className="results-phase">
          <CoOpVictoryOverlay
            victory={battleResult.victory}
            stars={battleResult.stars}
            creatureChanges={creatureChanges}
            currentUserId={user?.uid || ''}
            onReturnToMap={handleReturnToMap}
          />
        </div>
      )}
    </div>
  );
};
